import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { Footer } from "@/components/Footer";
import { JujuModal } from "@/components/JujuModal";
import { Sparkle } from "@/components/Sparkle";

const EASE = [0.16, 1, 0.3, 1];
const SIZES = ["XS", "S", "M", "L", "XL"];

export default function ModelPage() {
  const [form, setForm] = useState({ full_name: "", instagram: "", size: "", height: "", city: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.instagram.trim() || !form.size) {
      setError("Name, Instagram and size are required.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await api.post("/model-apply", form);
      setSubmitted(true);
      setShowModal(true);
    } catch {
      setError("Something glitched — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain min-h-screen">
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute left-1/2 top-[-15%] h-[50vh] w-[70vw] -translate-x-1/2 rounded-full bg-white/50 blur-[130px]" />
      </div>

      <div className="mx-auto max-w-6xl px-5 pt-8">
        <Link
          to="/"
          data-testid="nav-home-link"
          className="btn-ghost-chrome inline-flex items-center gap-2 px-5 py-2 text-xs font-semibold"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Good Juju
        </Link>
      </div>

      <section data-testid="model-hero" className="relative mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-5 py-16">
        <Sparkle className="sparkle pointer-events-none absolute left-[8%] top-[12%] h-6 w-6 text-white" />
        <Sparkle className="sparkle pointer-events-none absolute right-[10%] top-[22%] h-4 w-4 text-white" style={{ animationDelay: "1s" }} />
        <Sparkle className="sparkle pointer-events-none absolute bottom-[16%] left-[14%] h-3 w-3 text-white" style={{ animationDelay: "1.8s" }} />

        <motion.img
          src={LOGO_URL}
          alt="Good Juju — liquid chrome logo"
          className="mb-2 w-44 mix-blend-multiply sm:w-56"
          draggable="false"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: EASE }}
        />

        <div className="overflow-hidden">
          <motion.h1
            className="mb-8 text-center font-sans text-xs font-normal uppercase tracking-[0.3em] text-white sm:text-sm"
            style={{ fontFamily: "Arial, sans-serif", textShadow: "0 2px 14px rgba(11, 36, 71, 0.3)" }}
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          >
            Model Good Juju
          </motion.h1>
        </div>

        <motion.div
          data-testid="model-application-form"
          className="glass-card w-full rounded-3xl p-6 sm:p-10"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.35 }}
        >
          <form onSubmit={submit} className="space-y-4">
            <input data-testid="model-name-input" className="juju-input" placeholder="Full Name" value={form.full_name} onChange={set("full_name")} />
            <input data-testid="model-instagram-input" className="juju-input" placeholder="Instagram — @yourhandle" value={form.instagram} onChange={set("instagram")} />
            <select data-testid="model-size-select" className="juju-input" value={form.size} onChange={set("size")}>
              <option value="" disabled>
                Apparel size
              </option>
              {SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <input data-testid="model-height-input" className="juju-input" placeholder='Height & measurements — 5&apos;10" / 32-24-35 (optional)' value={form.height} onChange={set("height")} />
            <input data-testid="model-city-input" className="juju-input" placeholder="Base city / country (optional)" value={form.city} onChange={set("city")} />
            {error && (
              <p data-testid="model-form-error" className="text-sm text-rose-600">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="model-apply-submit-btn"
              disabled={loading || submitted}
              className="btn-chrome w-full px-6 py-4 text-sm disabled:opacity-70"
            >
              {submitted ? "Submitted" : loading ? "Sending..." : "Submit Application"}
            </button>
          </form>
        </motion.div>

      </section>

      <Footer />
      <JujuModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Application Submitted"
        message="Good, Juju will be in touch ;)"
        popupTestId="model-success-popup"
        messageTestId="model-popup-message"
      />
    </div>
  );
}
